import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { userService } from '../../_services';
import { Redirect } from "react-router-dom";
import ColorJson from '../../../src/whitelabel.json';
import { cn, safeParseDate } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import FilterTagCom from '../FilterTag';

// New modern widget components
import BarListChart from '../BarListChart';
import ColumnPieChart from '../ColumnPieChart';
import ListChartWidget from '../ListChartWidget';
import MultiStackBarChart from '../MultiStackBarChart';
import MultiLineChart from '../MultiLineChart';

// Legacy components (to be migrated)
import Claim from '../Claim';

function WholesalerDashboard({ 
    dashboardName: propDashboardName, 
    startDate: propStartDate, 
    endDate: propEndDate, 
    handeChangefilter,
    filterUpdateTrigger, 
    handleAllDirectOverride_user,
    dist_usertype: propDistUserType 
}) {
    // Calculate default dates
    const getDefaultDates = () => {
        const defaultStartDate = new Date();
        defaultStartDate.setDate(defaultStartDate.getDate() - 30);
        const defaultEndDate = new Date();
        return { defaultStartDate, defaultEndDate };
    };

    // Get stored dates from localStorage
    const getStoredDates = () => {
        const { defaultStartDate, defaultEndDate } = getDefaultDates();
        const storedStartDate = localStorage.getItem('User_Start_Date');
        const storedEndDate = localStorage.getItem('User_End_Date');
        
        const startdate = (storedStartDate && storedStartDate !== "" && storedStartDate !== "null")
            ? safeParseDate(storedStartDate, defaultStartDate)
            : defaultStartDate;
        const enddate = (storedEndDate && storedEndDate !== "" && storedEndDate !== "null")
            ? safeParseDate(storedEndDate, defaultEndDate)
            : defaultEndDate;
        
        return { startdate, enddate };
    };

    // Get user role info
    const getUserRoleInfo = () => {
        let roletype = '', rolename = '', whId = 0;
        try {
            const userdata = JSON.parse(localStorage.getItem('user'));
            if (userdata.userrole === "Distributor" && userdata.wholesaler === "Avenelllllll") {
                roletype = "avdistributor";
                rolename = userdata.email;
                whId = userdata.wholesaler_Id;
            } else if (userdata.userrole === "Distributor" && userdata.wholesaler !== "Avenelllll") {
                roletype = "distributor";
                rolename = userdata.paramsname;
                whId = userdata.wholesaler_Id;
            } else {
                roletype = userdata.userrole;
                rolename = userdata.paramsname;
                whId = userdata.wholesaler_Id;
            }
        } catch (error) { }
        return { roletype, rolename, whId };
    };

    // Get initial filter data
    const getInitialFilterData = () => {
        let filterdatalocal = localStorage.getItem('Advance_Filter');
        if (filterdatalocal == null || filterdatalocal === '' || filterdatalocal === undefined || filterdatalocal === "null") {
            filterdatalocal = '';
        }
        return filterdatalocal;
    };

    // Initialize state
    const { startdate, enddate } = getStoredDates();
    const { roletype, rolename, whId } = getUserRoleInfo();
    const userdata = JSON.parse(localStorage.getItem('user') || '{}');

    const [state, setState] = useState({
        userdata: userdata,
        scrollArray: {},
        graphData: [],
        dashboardName: propDashboardName,
        endDate: format(enddate, "yyyyMMdd"),
        startDate: format(startdate, "yyyyMMdd"),
        FilterTagMatch: [],
        FilterTagList: getInitialFilterData(),
        roletype: roletype,
        rolename: rolename,
        wh_Id: whId,
        dist_usertype: propDistUserType,
        prescribe_status: false
    });

    // Refs
    const resizeHandlerRef = useRef(null);
    const prevDistUserTypeRef = useRef(propDistUserType);

    // Get colors based on domain
    const getColors = useCallback(() => {
        const host = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        const isRxHacker = host === "rxhacker.com" || 
                          host === "localhost:5464" || 
                          window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
                          window.location.host.toLowerCase() === "reporting.nulifespanrx.com";
        
        if (isRxHacker) {
            return {
                primary: ColorJson.ApplicationColor.Rxhacker_primary_color,
                secondary: ColorJson.ApplicationColor.Rxhacker_secondary_color,
                extra: ColorJson.ApplicationColor.Rxhacker_extra_color
            };
        }
        return {
            primary: ColorJson.ApplicationColor.Glic_primary_color,
            secondary: ColorJson.ApplicationColor.Glic_secondary_color,
            extra: ColorJson.ApplicationColor.Glic_extra_color
        };
    }, []);

    // Fetch prescriber info
    const fetchPrescriberInfo = useCallback(() => {
        try {
            const whid = state.userdata.wholesaler_Id?.toString();
            if (whid) {
                userService.WhPrescribeInfo(whid).then(apidata => {
                    try {
                        if (apidata && apidata.length > 0) {
                            setState(prev => ({ ...prev, prescribe_status: apidata[0]._IsPrescribeData }));
                        }
                    } catch (error) { }
                });
            }
        } catch (error) { }
    }, [state.userdata.wholesaler_Id]);

    // Build graph data
    const buildGraphData = useCallback((dname, stdate, endate) => {
        let APIBaseURL = "";
        let wname = dname || propDashboardName;
        let startDateObj = stdate || propStartDate;
        let endDateObj = endate || propEndDate;
        const AllDirectOverride = propDistUserType;

        const startDate = format(safeParseDate(startDateObj), "yyyyMMdd");
        const endDate = format(safeParseDate(endDateObj), "yyyyMMdd");

        if (AllDirectOverride === 'All') {
            APIBaseURL = '/api/ws_All_Filter';
        } else if (AllDirectOverride === 'Direct') {
            APIBaseURL = '/api/ws';
        } else if (AllDirectOverride === 'Override') {
            APIBaseURL = '/api/ws_Override_Filter';
        } else {
            APIBaseURL = '/api/ws';
        }

        let AdFilterJson = '';
        try {
            AdFilterJson = JSON.parse(localStorage.getItem('Advance_Filter'));
        } catch (error) { }

        const filterTagList = (AdFilterJson != null && AdFilterJson !== '' && AdFilterJson !== undefined && AdFilterJson !== "null")
            ? JSON.stringify(AdFilterJson)
            : '';

        const { primary: primaryColor, secondary: secondaryColor, extra: extraColor } = getColors();
        const wholesalerId = state.userdata?.wholesaler_Id;
        const filterTime = localStorage.getItem('Filter_Last_Update_Time');
        const hdType = localStorage.getItem('Updated_HD_Type');

        // Request JSON builders
        const buildRequestJson = (chartType, rcount = 30, extra = {}) => {
            return JSON.stringify({
                wname: String(wholesalerId),
                start_date: startDate,
                end_date: endDate,
                rcount: rcount,
                chart_type: chartType,
                filtertime: filterTime,
                rxsClaimType: hdType,
               // wholesaler_id: wholesalerId,
                ...extra
            });
        };

        const Column_PostData_RequestJson_Commission = buildRequestJson("columnlist_multiaxislist", 0, { ispatientsaving: "true", pharmacynumber: "all" });
        const Column_PostData_RequestJson = buildRequestJson("columnList", 30, { ispatientsaving: "true", pharmacynumber: "all" });
        const PostData_RequestJson = buildRequestJson("pielist", 30);
        const PostData_PatientSaving_True = buildRequestJson("columnList", 0, { ispatientsaving: "true", pharmacynumber: "all" });
        const PostData_PatientSaving_False = buildRequestJson("columnList", 0, { ispatientsaving: "false", pharmacynumber: "all" });
        const Table_Pharmacy_RequestJson = buildRequestJson("list", 30);
        const TotalTransaction_PostData_RequestJson = buildRequestJson("columnList", 0, { ispatientsaving: "true", pharmacynumber: "all" });
        const Claim_PostData_RequestJson = buildRequestJson("list", 0);
        const MultiAxis_Column_PostData_RequestJson = buildRequestJson("column_multiaxislist", 0, { ispatientsaving: "true", pharmacynumber: "all" });
        const MultiLine_PostData_RequestJson = buildRequestJson("multilinelist", 0, { ispatientsaving: "true", pharmacynumber: "all" });

        const isLegacyUser = state.userdata.isLegacyUser === true;

        // Common widgets
        const baseWidgets = [
            {
                url: APIBaseURL + "/totlal_transactions_new",
                type: "Bar_List",
                title: "Total Transactions",
                Subtitle: "Total number of transactions for each day",
                ColorCode: secondaryColor,
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "# of Transactions",
                interval: 'day',
                id: "WTotalTransaction",
                RequestJson: TotalTransaction_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/new_and_repeat_claims_Line",
                type: "MultiLine_List",
                title: "New vs Repeat Transactions",
                Subtitle: "New vs Repeat Compensable Transactions",
                ColorCode: primaryColor,
                DataType: "NA",
                interval: 'day',
                XlabelString: "Date",
                YlabelString: "# of Transactions",
                id: "new_repeat_widget_Line",
                BorderRadius: 0,
                RequestJson: MultiLine_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/metric1a_new",
                type: "Bar_List",
                title: "Transactions by Group Number",
                Subtitle: "Number of transactions for each group number",
                ColorCode: secondaryColor,
                DataType: "GroupNumber",
                XlabelString: "Group Number",
                YlabelString: "# of Transactions",
                id: "WMetric1a",
                RequestJson: Column_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/patient_savings_new",
                type: "Bar_List",
                title: "Total Patient Savings ($)",
                Subtitle: "Total savings for all patients in $",
                ColorCode: secondaryColor,
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "Total Savings",
                id: "WMetric3B",
                RequestJson: PostData_PatientSaving_True,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/drugtransaction_new",
                type: "Column_Pie",
                title: "Transactions by Drug",
                Subtitle: "Number of transactions for each drug",
                ColorCode: extraColor,
                DataType: "DrugName",
                id: "WDrugTransaction",
                RequestJson: PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/commission_new",
                type: "MultiBar_List",
                title: "Total Revenue Earned",
                Subtitle: "Revenue earned for each month",
                ColorCode: primaryColor,
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "Commission",
                interval: 'month',
                id: "WCommission",
                RequestJson: MultiAxis_Column_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/prescribe_transaction_new",
                type: "List",
                title: "Transactions by Prescriber",
                Subtitle: "Number of transactions at each Prescriber",
                ColorCode: "#ffba10", DataType: "PrescriberName",
                XlabelString: "",
                YlabelString: "",
                id: "WPrescriberTransaction1",
                RequestJson: Table_Pharmacy_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                // Always use /api/ws for unique_Members_Widget since it's not available in ws_All_Filter or ws_Override_Filter
                url: "/api/ws/unique_Members_Widget",
                type: "Bar_List",
                title: "Unique Member ID's",
                Subtitle: "Number of unique members transacted on each day",
                ColorCode: secondaryColor,
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "# of Unique Member ID's",
                interval: 'day',
                id: "WUniqueMemberID",
                RequestJson: TotalTransaction_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            }
        ];

        // Add legacy user specific widgets
        if (isLegacyUser) {
            // Insert Claim widget after first widget for legacy users
            baseWidgets.splice(1, 0, {
                url: APIBaseURL + "/ClaimCalculation",
                type: "Claim",
                title: "Claim Counts",
                Subtitle: "",
                ColorCode: extraColor,
                id: "RxSensClaim",
                RequestJson: Claim_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            });
        }

        return { graphData: baseWidgets, filterTagList, startDate, endDate, dashboardName: wname };
    }, [propDashboardName, propStartDate, propEndDate, propDistUserType, state.userdata, getColors]);

    // Change data handler
    const changeData = useCallback((dname, stdate, endate) => {
        fetchPrescriberInfo();
        const result = buildGraphData(dname, stdate, endate);
        setState(prev => ({
            ...prev,
            graphData: result.graphData,
            FilterTagList: result.filterTagList,
            startDate: result.startDate,
            endDate: result.endDate,
            dashboardName: result.dashboardName
        }));
    }, [fetchPrescriberInfo, buildGraphData]);

    // Handle filter change
    const handlechangea = useCallback((e) => {
        
        // If e has heading and value properties, it's from a chart click
        if (e && e.heading && e.value) {
            // Validate heading is not undefined or invalid
            if (e.heading === 'undefined' || !e.heading) {
                return;
            }
            
            try {
                let Created_Advance_Filter = JSON.parse(localStorage.getItem('Advance_Filter')) || {};
                
                // Add or update the filter
                if (Created_Advance_Filter[e.heading]) {
                    if (!Created_Advance_Filter[e.heading].includes(e.value)) {
                        Created_Advance_Filter[e.heading].push(e.value);
                    }
                } else {
                    Created_Advance_Filter[e.heading] = [e.value];
                }
                
                localStorage.setItem('Advance_Filter', JSON.stringify(Created_Advance_Filter));
                localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
                localStorage.setItem('Updated_AdFilter_Status', JSON.stringify(Created_Advance_Filter));
                
            } catch (error) {
            }
        }
        
        if (handeChangefilter) {
            handeChangefilter(e);
        }
    }, [handeChangefilter]);

    // Handle collapse (kept for backwards compatibility)
    const handleCollapse = useCallback(() => {
        // Sidebar collapse is now handled by the PagesWrapper component
    }, []);

    // Initialize on mount
    useEffect(() => {
        localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
        changeData();

        // Handle responsive layout
        const handleResize = () => {
            const iframes = document.querySelectorAll('.widget-iframe');
            iframes.forEach(iframe => {
                try {
                    const body = iframe.contentDocument?.body;
                    if (body) {
                        if (window.innerWidth < 480) {
                            body.classList.add('scrollMobile');
                        } else {
                            body.classList.remove('scrollMobile');
                        }
                    }
                } catch (e) { /* cross-origin */ }
            });
        };

        const timeoutId = setTimeout(handleResize, 1000);
        window.addEventListener('resize', handleResize);
        resizeHandlerRef.current = handleResize;

        return () => {
            clearTimeout(timeoutId);
            if (resizeHandlerRef.current) {
                window.removeEventListener('resize', resizeHandlerRef.current);
            }
        };
    }, []);

    // Handle dist_usertype changes
    useEffect(() => {
        if (prevDistUserTypeRef.current !== propDistUserType) {
            prevDistUserTypeRef.current = propDistUserType;
            setState(prev => ({ ...prev, dist_usertype: propDistUserType }));
            localStorage.setItem('dist_usertype', propDistUserType);
            changeData();
        }
    }, [propDistUserType, changeData]);

    // Check for filter/date changes and refresh data
    useEffect(() => {
        if (!propStartDate || !propEndDate) return;

        const startDate = format(safeParseDate(propStartDate), "yyyyMMdd");
        const endDate = format(safeParseDate(propEndDate), "yyyyMMdd");

        let IsCall = false;
        try {
            const prevFilter = localStorage.getItem('Prev_Filter_Status')?.toLowerCase();
            const updatedFilter = localStorage.getItem('Updated_Filter_Status')?.toLowerCase();
            const prevAdFilter = localStorage.getItem('Prev_AdFilter_Status');
            const updatedAdFilter = localStorage.getItem('Updated_AdFilter_Status');
            const prevHdType = localStorage.getItem('Prev_HD_Type');
            const updatedHdType = localStorage.getItem('Updated_HD_Type');

            if (prevFilter !== updatedFilter || prevAdFilter !== updatedAdFilter || prevHdType !== updatedHdType) {
                IsCall = true;
            }
        } catch (error) {
            IsCall = false;
        }

        if (endDate !== state.endDate || startDate !== state.startDate || IsCall) {
            if (IsCall) {
                localStorage.setItem('Prev_Filter_Status', localStorage.getItem('Updated_Filter_Status'));
                const updatedAdFilter = localStorage.getItem('Updated_AdFilter_Status');
                if (updatedAdFilter === "null" || updatedAdFilter === null) {
                    localStorage.setItem('Prev_AdFilter_Status', null);
                    localStorage.setItem('Updated_AdFilter_Status', null);
                } else {
                    localStorage.setItem('Prev_AdFilter_Status', updatedAdFilter);
                }
                const updatedHdType = localStorage.getItem('Updated_HD_Type');
                if (updatedHdType === "null" || updatedHdType === null) {
                    localStorage.setItem('Prev_HD_Type', null);
                    localStorage.setItem('Updated_HD_Type', null);
                } else {
                    localStorage.setItem('Prev_HD_Type', updatedHdType);
                }
            }
            changeData();
        }
    }, [propStartDate, propEndDate, state.startDate, state.endDate, changeData, filterUpdateTrigger]);

    // Check user permissions
    try {
        const usertype = userService.Get_User_Type();
        if (userService.Check_LoggedIn_Status() === 'Login') {
            return <Redirect to="/login" />;
        }
        if (usertype?.toLowerCase() === 'admin') {
            return <Redirect to='/pageNotFound' />;
        } else if (usertype?.toLowerCase() === 'distributor') {
            return <Redirect to='/pageNotFound' />;
        } else if (usertype?.toLowerCase() === 'subadmin') {
            return <Redirect to='/pageNotFound' />;
        }
    } catch (error) { }

    // Widget visibility helpers
    const isWidgetVisible = (item) => {
        const dashboardNameLower = state.dashboardName?.replace(" Dashboard (wholesaler)", "").toLowerCase() || '';
        
        // Matching production-v3 logic exactly:
        // ((dashboardName != 'patient rx discount' && item.title == "Unique Member ID's") && 
        //  (dashboardName != 'id.me' && item.title == "Unique Member ID's") || 
        //  (prescribe_status == false && item.title == "Transactions by Prescriber")) ? null : render
        
        const hideUniqueMemberIDs = (dashboardNameLower !== 'patient rx discount' && item.title === "Unique Member ID's") && 
                                    (dashboardNameLower !== 'id.me' && item.title === "Unique Member ID's");
        
        const hideTransactionsByPrescriber = (state.prescribe_status === false && item.title === "Transactions by Prescriber");
        
        if (hideUniqueMemberIDs || hideTransactionsByPrescriber) {
            return false;
        }
        
        return true;
    };

    const isFullWidth = (itemId) => {
        const fullWidthTypes = [
            'WTotalTransaction', 'new_repeat_widget', 'WUniqueMemberID', 
            'RxSensClaim', 'RxSensClaimNew', 'WMetric1a', 'WPharmacyTransaction1', 
            'WPrescriberTransaction1', 'WRepeat_PrescriptionsTransaction', 
            'WMetric3B', 'WDrugTransaction', 'WCommission', 'new_repeat_widget_Line'
        ];
        return fullWidthTypes.includes(itemId);
    };

    // Render widget content based on type
    const renderWidgetContent = (item, index) => {
        switch (item.type) {
            // Modern widget components
            case "Bar_List":
                return <BarListChart data={item} key={"barlistgraph" + index} _Index={index} handlechangea={handlechangea} />;
            case "Column_Pie":
                return <ColumnPieChart data={item} key={"columnpiegraph" + index} _Index={index} handlechangea={handlechangea} />;
            case "List":
                return <ListChartWidget data={item} key={"listgraph" + index} _Index={index} handlechangea={handlechangea} />;
            case "MultiBar_List":
                return <MultiStackBarChart data={item} key={"barlistgraph1" + index} _Index={index} />;
            case "MultiLine_List":
                return <MultiLineChart data={item} key={"multilinelistgraph1" + index} _Index={index} />;
            
            // Legacy components
            case "Claim":
                return <Claim data={item} key={"claimbox" + index} _Index={index} />;
            default:
                return null;
        }
    };

    return (
        <div id="dashboard-container" className={cn("space-y-6", state.userdata?.isLegacyUser === false && "legacy")}>
            {/* Filter Tags */}
            <FilterTagCom 
                Rolename={state.rolename} 
                wh_Id={state.wh_Id} 
                Roletype={state.roletype} 
                df={state.startDate} 
                dt={state.endDate} 
                handlechangea={handlechangea} 
                FilterTags={state.FilterTagList} 
                Loading={true} 
            />

            {/* Dashboard Widgets Grid */}
            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
                {state.graphData.length === 0 ? (
                    Array.from({ length: 8 }).map((_, i) => (
                        <div key={"skeleton-" + i} className="min-h-[420px]">
                            <Skeleton className="h-full w-full rounded-xl" />
                        </div>
                    ))
                ) : (
                    state.graphData.map((item, index) => {
                        if (!isWidgetVisible(item)) {
                            return null;
                        }
                        return (
                            <div 
                                key={"graph-widget" + index}
                                id={item.id + index}
                                className={cn(
                                    item.type !== "Claim" && item.type !== "Claim_New" && "min-h-[420px]",
                                    isFullWidth(item.id) && "2xl:col-span-2"
                                )}
                            >
                                {renderWidgetContent(item, index)}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default WholesalerDashboard;
